export const paths = {
  index: '/',
  // BOPS-182: public, unauthenticated recipient invoice-download page (rendered before the auth
  // gate in app.tsx). The emailed link is `…/invoice-download?t=<token>`.
  invoiceDownload: '/invoice-download',
  dashboard: '/dashboard',
  dsn: '-dsn',
  departmentDashboard: '/department-dashboard',
  departmentComplianceAlerts: '/department-compliance-alerts',
  departmentMetrics: '/department-metrics',
  departmentDownloads: '/department-downloads',
  mdsDashboard: '/mds-dashboard',
  mdsMetrics: '/mds-metrics',
  restoreSkill: '/restore-skill',
  monthDayAlert: 'monthly-closeout-alerts/month-day-alert',
  monthlyCloseoutAlert: '/monthly-closeout-alerts',
  preliminaryInvoiceReview: 'monthly-closeout-alerts/preliminary-invoice-review',
  restoreCare: '/restore-care',
  restoreWellness: 'restore-wellness',
  reports: '/reports',
  patients: '/patients',
  patientDetails: 'patient-details',
  createPatient: 'add-new-patient',
  addNewScreening: '/add-new-screening',
  addNewEpisodeOfCare: '/add-new-episode-of-care',
  editEpisodeOfCare: '/edit-episode-of-care',
  screening: '/screening',
  sreeningDetails: '/screening-details',
  editPatient: '/edit-patient',
  deactivate: 'deactivate',
  episodeOfCare: '/episode-care',
  postAuthResetPassword: '/post-auth-forgot-password',
  // APIT-696 — landing route for the emailed magic-link reset. Authenticated (the user arrives
  // with a fresh Keycloak session); sits alongside postAuthResetPassword as the only other
  // authenticated route outside DashboardLayout. See routes.tsx and app.tsx.
  passwordResetComplete: '/password-reset-complete',
  laborManagement: {
    index: '/labor-management',
    myAvailability: 'my-availability',
    checkInCheckOut: 'check-in-check-out',
    laborReportAndApproval: 'labor-report-approval',
    laborHoursManagement: 'labor-hours-management',
    npcLog: 'npc-log',
  },
  auth: {
    login: '/auth/login',
    resetPassword: '/auth/reset-password',
    // APIT-696 — was '/forgot-password', which does not match the registered route
    // ('auth' + 'forgot-password' -> '/auth/forgot-password') and had no other reader.
    // Corrected so app.tsx's signed-out exemption and the page's own "back to login"
    // link both key off the real path.
    forgotPassword: '/auth/forgot-password',
  },
  docs: 'https://material-kit-pro-react-docs.devias.io',
  schedule: {
    index: '/scheduling',
    therapistSchedule: 'therapist',
    therapyPlanning: {
      index: 'therapy-planning',
      details: '/details',
      info: '/info',
      editDetails: '/edit-details',
    },
  },
  editFacilityStay: '/edit-facility-stay',
  addFacilityStay: 'add-facility-stay',
  details: 'details',
  therapyPlanning: {
    index: '/scheduling/therapy-planning',
    details: '/details',
    info: '/info',
    editDetails: '/edit-details',
    authorization: '/authorization',
    editAuthorization: '/edit-authorization',
  },
  facilityStay: 'facility-stay',
  dailySessionNotes: '/daily-session-notes',
  landingPage: '/auth/landing-page',
  landingPageLogout: '/auth/logout-landing',
  previewDocument: '/preview-document',
  editDocument: '/edit-document',
  createDocument: '/create-document',
  physicianSign: '/physician-sign-document',
  getSignaturePreview: '/signature-preview',
  // getSignaturePreviewOld:'/signature-preview-dsn',
  finalizeNotes: '/finalize-soap-notes',
  redirectSupervisor: '/redirect-location',
  physicianDashboard: '/physician-dashboard',
  physicianDocumentsSign: `/physician-dashboard/documents-sign`,
  payers: '/payers',
  addNewPayer: '/add-new',
  mdsAssessments: '/mds-assessments',
  createMdsAssessment: '/create-mds-assessment',
};

// NOTE (THIN-8077): display names, NOT an enforcement surface. Gating goes through
// `usePermission(Permissions.X)` from src/rbac — no production code reads this object, and none
// should; the remaining readers are test helpers and docs.
//
// The values below MUST stay byte-equal to the installed @restore/permission-catalog, which
// src/rbac/rbac-parity.test.ts asserts. THIN-8077 renames 29 of these in the restore-core catalog
// (e.g. 'Read Daily Task' -> 'View Scheduling Board'). Update this map in the SAME change that
// bumps the @restore/permission-catalog dependency, never before it — leading the package breaks
// parity, and every `withPermissions([permissions.X])` helper seeds a name the catalog disagrees
// with.
export const permissions = {
  READ_PATIENT: 'View Patients',
  UPDATE_PATIENT: 'Update/Edit Patient Record',
  CREATE_PATIENT: 'Create Patient',
  ACTIVATE_PATIENT: 'Activate Patient',
  DEACTIVATE_PATIENT: 'Deactivate Patient',
  READ_DASHBOARD: 'View My Day Dashboard',
  READ_DEPARTMENT_DASHBOARD: 'Read Dashboard',
  READ_MDS_DASHBOARD: 'View MDS Dashboard',
  READ_SCREENINGS: 'View Screenings',
  CREATE_SCREENINGS: 'Create Screening',
  UPDATE_SCREENINGS: 'Update Screening',
  READ_EPISODE_OF_CARE: 'View Episodes of Care',
  CREATE_EPISODE_OF_CARE: 'Create Episode of Care',
  UPDATE_EPISODE_OF_CARE: 'Update/Edit Episode of Care',
  CLOSE_EPISODE_OF_CARE: 'Close Episode Of Care',
  READ_DAILY_SESSION_NOTES: 'View Daily Notes',
  UPDATE_DAILY_SESSION_NOTES: 'Update Daily Notes',
  READ_THERAPIST_PLANNING: 'View Therapy Planning',
  UPDATE_THERAPIST_PLANNING: 'Update/Edit Therapy Planning',
  READ_THERAPIST_SCHEDULING: 'View Scheduling Board',
  ADD_DAILY_TASK: 'Add Task',
  UPDATE_DAILY_TASK: 'Update/Edit Task',
  CREATE_AVAILABILITY: 'Create Availability',
  UPDATE_AVAILABILITY: 'Update Availability',
  READ_MY_AVAILABILITY: 'View My Availability',
  READ_MONTH_END_ALERT: 'View Month End Alerts',
  CREATE_RECERTIFICATION: 'Create Recertification',
  READ_RECERTIFICATION: 'View Recertifications',
  UPDATE_RECERTIFICATION: 'Update Recertification',
  READ_EVAL: 'View Evaluations',
  UPDATE_EVAL: 'Update Eval',
  CREATE_EVAL: 'Create Eval',
  READ_PTO: 'View PTO Requests',
  CREATE_PTO: 'Request PTO',
  LABOR_MANAGEMENT: 'Labor Management',
  LABOR_MANAGEMENT_ADD: 'Create Labor Management',
  LABOR_MANAGEMENT_DELETE: 'Delete Labor Management',
  LABOR_MANAGEMENT_UPDATE: 'Update Labor Management',
  READ_JURY_CODE: 'Apply Jury Duty Labor Code',
  READ_PTO_CODE: 'Apply PTO Labor Code',
  READ_HOL_CODE: 'Apply Holiday Labor Code',
  READ_PRNEDU_CODE: 'Apply PRN Education Labor Code',
  READ_REHAV_TRAV_CODE: 'Apply Rehab Travel Labor Code',
  READ_BEREV_CODE: 'Apply Bereavement Labor Code',
  READ_FLOAT_CODE: 'Apply Float Labor Code',
  EDIT_MEDICAL_CODE: 'Edit Medical Codes',
  THERAPY_TEMPLATE: 'Planner Templates',
  PRODUCTIVITY_LABOR_MATRIX: 'Productivity and Labor Metrics',
  ADD_ANNOUNCEMENT: 'Add Scheduler Announcement',
  CREATE_REPORT: 'Create Report',
  THERAPIST_DOC_DUE_FILTER: 'Default Filter For My Dashboard Therapist Docs Due',
  DOR_DOC_DUE_FILTER: 'Show Docs Due for All Disciplines',
  THOUGHT_SPOT_INTEGRATION_DOR: 'ThoughtSpot Integration DOR',
  OVERRIDE_RECHECK_DISCHARGE: 'Override ReCheck for Discharge',
  UNLOCK_DOCUMENT: 'Unlock Document',
  CERTIFY_SIGN_MONTH_END_ALERT: 'Certify and Sign Month End Alerts',
  SIGN_ON_BEHALF: 'Sign On Behalf - My Discipline',
  SPLIT_TASK_MIN: 'Split Task Minutes',
  THOUGHT_SPOT_INTEGRATION_THERAPIST: 'ThoughtSpot Integration Therapist',
  DEPARTMENT_METRICS: 'Department Metrics',
  VIEW_OTHER_THERAPIST_AVAILABILITY: 'View Department Availability',
  MDS_ASSESSMENT_COMMUNICATION: 'MDS Assessment Communication',
  READ_REPORT: 'Read Report',
  ADD_SCHEDULER_NOTE: 'Add Scheduler Note',
  ACCESS_TO_PATIENT_MDS_TAB: 'View Patient MDS Tab',
  REOPEN_CLOSED_MONTH: 'Reopen a Closed Month',
  DELETE_DAILY_NOTES: 'Delete Any Daily Note',
  HOLD_DAILY_NOTE_FROM_BILLING: 'Hold Daily Note From Billing',
  ARCHIVE_DAILY_NOTE: 'Archive Any Daily Note',
  FIRST_SIGN_OFF_PRELIMINARY_INVOICE_REVIEW: 'First Sign Off Preliminary Invoice Review',
  SECOND_SIGN_OFF_PRELIMINARY_INVOICE_REVIEW: 'Second Sign Off Preliminary Invoice Review',
};
